import React, { useEffect, useState } from 'react'
import { PatientTable } from '@components/tables/PatientTable'
import { Member, PatientDto } from '@services/api'
import {
  Box,
  Button,
  Typography,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Users, Trash2 } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { doGetPatientInsHistory } from '@state/reducers/patientsReducer'
import {
  InsiService,
  BulkVerifyInsiDTO,
  CheckInsiDTO,
  PatientInsHistory,
  CreateInsiBatchCheckRequestDTO,
  InsiBatchResultRequest,
  InsiVerificationBatchRequestDTO,
} from '@services/InsiService'
import moment from 'moment'
import { InsiBatchRequestList } from '@components/insi/InsiBatchRequestList'
import {
  createInsiBatchCheckRequest,
  findPendingInsiBatchRequests,
  updateInsiBatchCheckRequestResult,
  getInsiBatchResult,
} from '@state/thunks/insiThunk'

export const PatientsContainer: React.FC = () => {
  const dispatch = useAppDispatch()
  const { memberSituation, pendingBatchRequests, site } = useAppSelector(
    (state) => ({
      memberSituation: state.billing.memberSituation,
      pendingBatchRequests: state.insi.pendingBatchRequests,
      site: (state.auth.user as any as Member).sites?.[0],
    }),
  )

  const { patients } = useAppSelector(({ patients }) => ({
    patients: patients.patients,
  }))

  const [selectedPatients, setSelectedPatients] = useState<PatientDto[]>([])
  const [isVerifying, setIsVerifying] = useState(false)
  const [verificationError, setVerificationError] = useState<string | null>(
    null,
  )
  const [verificationSuccess, setVerificationSuccess] = useState<string | null>(
    null,
  )
  const [retrievingBatchId, setRetrievingBatchId] = useState<string | null>(
    null,
  )
  const [batchResultError, setBatchResultError] = useState<string | null>(null)
  const [batchResultSuccess, setBatchResultSuccess] = useState<string | null>(
    null,
  )

  const handlePatientSelection = (patients: PatientDto | PatientDto[]) => {
    setSelectedPatients(Array.isArray(patients) ? patients : [patients])
  }

  const handleClearSelection = () => {
    setSelectedPatients([])
    setVerificationError(null)
    setVerificationSuccess(null)
  }

  const handleBulkVerifyIns = async () => {
    setVerificationError(null)
    setVerificationSuccess(null)
    setBatchResultError(null)
    setBatchResultSuccess(null)

    if (!memberSituation) {
      setVerificationError(
        'Veuillez sélectionner un médecin disposant d\'une situation valide avant de vérifier l\'INSi',
      )
      return
    }

    if (selectedPatients.length === 0) {
      setVerificationError('Veuillez sélectionner au moins un patient')
      return
    }

    try {
      setIsVerifying(true)

      const insHistories = await Promise.all(
        selectedPatients.map((p) =>
          dispatch(doGetPatientInsHistory(p.id)).unwrap(),
        ),
      )

      const patientsWithIns: Map<
        number,
        {
          patientData: PatientDto
          insHistory: PatientInsHistory | null
        }
      > = new Map()

      selectedPatients.forEach((p) => {
        patientsWithIns.set(p.id, {
          patientData: p,
          insHistory: insHistories.find((h) => h?.patient_id === p.id) || null,
        })
      })

      const sanitize = (value?: string | number | null) =>
        value !== undefined && value !== null
          ? String(value).replace(/\s/g, '')
          : ''

      const psModel = {
        psNumeroFacturation: memberSituation.numIdentificationPSFacturation,
        psIdentStructure: memberSituation.numIdentificationStructure,
        authentificationMode: parseInt(
          process.env.REACT_APP_AUTH_MODE || '1',
          10,
        ),
        codePorteurCPS: process.env.REACT_APP_CODE_PORTEUR_CPS || '0',
      }

      const listeVerification = Array.from(patientsWithIns.entries()).reduce(
        (acc, [_, value]) => {
          const patient = value.patientData
          const history = value.insHistory

          if (!history || !patient) {
            return acc
          }
          const couverture = patient.couverture?.[0]
          let birthDate = ''

          const beneficiareDateNaissance = couverture?.beneficaireDateNaissance
          if (beneficiareDateNaissance) {
            birthDate = `${beneficiareDateNaissance.substring(
              0,
              4,
            )}-${beneficiareDateNaissance.substring(
              4,
              6,
            )}-${beneficiareDateNaissance.substring(6)}`
          } else if (patient.birthDate) {
            birthDate = moment(patient.birthDate).format('YYYY-MM-DD')
          }

          acc.push({
            patientId: patient.id,
            assureNumSecu: sanitize(patient.ssn),
            beneficiaireNom:
              couverture?.beneficiaireNom || patient.lastName || '',
            beneficiaireNomUsage:
              couverture?.beneficaireNomUsuel || patient.lastName || '',
            beneficiairePrenom:
              couverture?.beneficiairePrenom || patient.firstName || '',
            beneficiaireSexe: patient.sex || '',
            beneficiaireLieuNaissance: patient.placeOfBirth,
            beneficiaireDateNaissance: birthDate,
            beneficiaireRang: couverture?.rangNaissance?.toString() || '',
            beneficiaireNumSecu: sanitize(patient.ssn),
            numeroSerieCV: '',
            oid: history.ins_oid,
            insaVerifier: history.ins_value,
          })
          return acc
        },
        new Array<CheckInsiDTO & { patientId: number }>(),
      )

      const bulkVerifyPayload: BulkVerifyInsiDTO = {
        psModel,
        listeVerification,
        fichierInjecte: {
          fichierBase64: '',
        },
        assertions: {
          assertionPs: '',
          assertionVitale: '',
          cpsBase64AuthCertificate: '',
          cpsBase64SignatureCertificate: '',
        },
      }

      const result = await InsiService.bulkVerifyIns(bulkVerifyPayload)

      const batchResponse = result?.message
      const globalError =
        batchResponse?.reponse?.erreur === true
          ? batchResponse.reponse?.erreurMessageComplet ||
            batchResponse.reponse?.erreurMessage ||
            'Erreur lors de la vérification INSi en masse'
          : null

      if (globalError) {
        setVerificationError(globalError)
        return
      }

      const batchId = batchResponse?.lot
      if (batchId) {
        const delayMoment = moment(batchResponse?.delai)

        const delayInSeconds =
          delayMoment.seconds() +
          delayMoment.minutes() * 60 +
          delayMoment.hours() * 3600

        const batchRequestPayload: CreateInsiBatchCheckRequestDTO = {
          batchId,
          delay: delayInSeconds,
          dateToStartResultCheck: batchResponse?.horodatageFin ?? '',
          codeReturned: batchResponse?.codeRetour ?? '',
          submittedList: listeVerification,
        }

        try {
          await dispatch(
            createInsiBatchCheckRequest(batchRequestPayload),
          ).unwrap()
        } catch (batchRequestError: any) {
          setVerificationError(
            batchRequestError?.message ||
              'Erreur lors de l\'enregistrement de la vérification INSi',
          )
          return
        }
      }
    } catch (error: any) {
      setVerificationError(
        error?.message || 'Erreur lors de la vérification INSi en masse',
      )
    } finally {
      setIsVerifying(false)
    }
  }

  useEffect(() => {
    dispatch(findPendingInsiBatchRequests())
  }, [dispatch])

  const handleRetrieveBatchResults = async (
    request: InsiVerificationBatchRequestDTO,
  ) => {
    setBatchResultError(null)
    setBatchResultSuccess(null)

    if (!memberSituation) {
      setBatchResultError(
        'Veuillez sélectionner un médecin disposant d\'une situation valide avant de récupérer les résultats INSi',
      )
      return
    }

    try {
      setRetrievingBatchId(request.batchId)

      const psModel: InsiBatchResultRequest['psModel'] = {
        psNumeroFacturation: memberSituation.numIdentificationPSFacturation,
        psIdentStructure: memberSituation.numIdentificationStructure,
        authentificationMode: '1',
      }

      const batchResultRequest: InsiBatchResultRequest = {
        psModel,
        lot: {
          idLot: request.batchId,
        },
        fichierInjecte: {
          fichierBase64: '',
        },
        assertions: {
          assertionPs: '',
          assertionVitale: '',
          cpsBase64AuthCertificate: '',
          cpsBase64SignatureCertificate: '',
        },
      }

      const batchResult = await dispatch(
        getInsiBatchResult({
          batchId: request.batchId,
          request: batchResultRequest,
        }),
      ).unwrap()
      if (batchResult.result.message.codeRetour === '02') {
        const delayMoment = moment(batchResult.result.message.delai)
        console.log(request.delay)
        debugger

        const delayInSeconds =
          delayMoment.seconds() +
          delayMoment.minutes() * 60 +
          delayMoment.hours() * 3600

        await dispatch(
          updateInsiBatchCheckRequestResult({
            batchId: request.batchId,
            payload: {
              delay: delayInSeconds,
            },
          }),
        )
        return
      }
      const resultEntries = batchResult.result?.message?.resultats ?? []

      if (resultEntries.length > 0) {
        await dispatch(
          updateInsiBatchCheckRequestResult({
            batchId: request.batchId,
            payload: { result: batchResult.result },
          }),
        ).unwrap()
        setBatchResultSuccess('Résultats INSi récupérés avec succès.')
      } else {
        setBatchResultSuccess(
          'La récupération est terminée mais aucun résultat détaillé n’a été fourni.',
        )
      }
    } catch (error: any) {
      setBatchResultError(
        error?.message || 'Erreur lors de la récupération des résultats INSi.',
      )
    } finally {
      setRetrievingBatchId(null)
    }
  }

  const minPatientsForBatch = site.insBatchVerifyMin
  const maxPatientsForBatch = site.insBatchVerifyMax

  const isInsSubmitEnabled =
    !isVerifying &&
    selectedPatients.length >= minPatientsForBatch &&
    selectedPatients.length <= maxPatientsForBatch

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col gap-4">
      {(verificationError ||
        verificationSuccess ||
        batchResultError ||
        batchResultSuccess) && (
        <Box
          sx={{
            marginTop: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {verificationError && (
            <Alert severity="error" onClose={() => setVerificationError(null)}>
              {verificationError}
            </Alert>
          )}
          {verificationSuccess && (
            <Alert
              severity="success"
              onClose={() => setVerificationSuccess(null)}
            >
              {verificationSuccess}
            </Alert>
          )}
          {batchResultError && (
            <Alert severity="error" onClose={() => setBatchResultError(null)}>
              {batchResultError}
            </Alert>
          )}
          {batchResultSuccess && (
            <Alert
              severity="success"
              onClose={() => setBatchResultSuccess(null)}
            >
              {batchResultSuccess}
            </Alert>
          )}
        </Box>
      )}
      {selectedPatients.length > 0 && (
        <Box
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            padding: 2,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Users size={20} />
            <Typography variant="body1" fontWeight="bold">
              {selectedPatients.length} patient
              {selectedPatients.length > 1 ? 's' : ''} sélectionné
              {selectedPatients.length > 1 ? 's' : ''}
            </Typography>
            {selectedPatients.map((patient) => (
              <Chip
                key={patient.id}
                label={`${patient.firstName} ${patient.lastName}`}
                onDelete={() => {
                  setSelectedPatients((prev) =>
                    prev.filter((p) => p.id !== patient.id),
                  )
                }}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  '& .MuiChip-deleteIcon': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': {
                      color: 'white',
                    },
                  },
                }}
              />
            ))}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              alignItems: 'flex-end',
            }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={
                  isVerifying ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Trash2 size={16} />
                  )
                }
                onClick={handleBulkVerifyIns}
                disabled={!isInsSubmitEnabled}
              >
                {isVerifying ? 'Vérification...' : 'Vérifier INSi'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearSelection}
                disabled={isVerifying}
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                Annuler
              </Button>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.85)',
                textAlign: 'right',
              }}
            >
              Minimum : {minPatientsForBatch} patients • Maximum :{' '}
              {maxPatientsForBatch} patients requis pour activer la vérification
              INSi
            </Typography>
          </Box>
        </Box>
      )}
      <div className="flex flex-1 gap-4">
        <InsiBatchRequestList
          requests={pendingBatchRequests}
          onRetrieveResults={handleRetrieveBatchResults}
          retrievingBatchId={retrievingBatchId}
          canRetrieve={Boolean(memberSituation)}
        />
        <div className="flex-1 flex">
          <PatientTable
            style={{
              height: '660px',
            }}
            multipleSelection={true}
            selectedRows={selectedPatients}
            onConfirm={handlePatientSelection}
            patients={patients}
          />
        </div>
      </div>
    </div>
  )
}
